import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Mail, ChevronLeft } from 'lucide-react';
import Footer from './Footer';

const CALENDLY = "https://calendly.com/arthur-vacogne-rdv/rdv-echange-1er-prise-de-contact";
const SITE_URL = "https://arthurvacogne.fr";

/**
 * Generic SEO landing page template.
 * Pages built with this template are NOT linked from the main navigation.
 * They exist only to rank in Google for long-tail keywords and to be reached
 * directly from search results.
 *
 * Props:
 *   slug       : URL path (without leading slash) — used for canonical
 *   title      : <title> tag content
 *   description: <meta description> content
 *   keywords   : <meta keywords> content
 *   h1         : Main page heading
 *   subtitle   : Short paragraph under the H1
 *   sections   : array of { title, body, bullets? }
 *   faq        : array of { q, a }
 *   schemaService : Service name for JSON-LD Service schema
 */
const SeoLandingPage = ({
  slug,
  title,
  description,
  keywords,
  h1,
  subtitle,
  sections = [],
  faq = [],
  schemaService
}) => {
  const canonical = `${SITE_URL}/${slug}`;

  // Set document head dynamically (no react-helmet dependency)
  useEffect(() => {
    const prevTitle = document.title;

    // Snapshot existing meta values so we can restore them on unmount
    const snapshot = (selector) => {
      const el = document.head.querySelector(selector);
      return el ? { el, attrs: Object.fromEntries(Array.from(el.attributes).map(a => [a.name, a.value])) } : null;
    };
    const prev = {
      description: snapshot('meta[name="description"]'),
      keywords: snapshot('meta[name="keywords"]'),
      canonical: snapshot('link[rel="canonical"]'),
      ogTitle: snapshot('meta[property="og:title"]'),
      ogDescription: snapshot('meta[property="og:description"]'),
      ogUrl: snapshot('meta[property="og:url"]')
    };

    document.title = title;

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag || 'meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => {
        if (k !== 'tag') el.setAttribute(k, v);
      });
      return el;
    };

    ensureMeta('meta[name="description"]', { tag: 'meta', name: 'description', content: description });
    ensureMeta('meta[name="keywords"]', { tag: 'meta', name: 'keywords', content: keywords });
    ensureMeta(`link[rel="canonical"]`, { tag: 'link', rel: 'canonical', href: canonical });
    ensureMeta('meta[property="og:title"]', { tag: 'meta', property: 'og:title', content: title });
    ensureMeta('meta[property="og:description"]', { tag: 'meta', property: 'og:description', content: description });
    ensureMeta('meta[property="og:url"]', { tag: 'meta', property: 'og:url', content: canonical });

    // Page-level JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.dataset.pageSchema = slug;
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${canonical}#webpage`,
          "url": canonical,
          "name": title,
          "description": description,
          "inLanguage": "fr-FR",
          "isPartOf": { "@id": `${SITE_URL}/#website` }
        },
        {
          "@type": "Service",
          "name": schemaService,
          "provider": { "@id": `${SITE_URL}/#business` },
          "areaServed": ["Annecy", "Haute-Savoie", "Savoie", "Isère", "Rhône", "Ain", "Auvergne-Rhône-Alpes", "France"],
          "description": description
        },
        {
          "@type": "FAQPage",
          "mainEntity": faq.map(({ q, a }) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a }
          }))
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
            { "@type": "ListItem", "position": 2, "name": h1, "item": canonical }
          ]
        }
      ]
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.title = prevTitle;
      // Restore original meta values
      const restore = (snap) => {
        if (!snap) return;
        Object.entries(snap.attrs).forEach(([name, value]) => snap.el.setAttribute(name, value));
      };
      restore(prev.description);
      restore(prev.keywords);
      restore(prev.canonical);
      restore(prev.ogTitle);
      restore(prev.ogDescription);
      restore(prev.ogUrl);
      if (schemaScript.parentNode) schemaScript.parentNode.removeChild(schemaScript);
    };
  }, [slug, title, description, keywords, h1, canonical, faq, schemaService]);

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh' }}>
      {/* Slim header with back-to-home only (not the full nav) */}
      <header style={{
        background: 'var(--primary-brown)',
        padding: '0.85rem 0',
        borderBottom: '1px solid rgba(172, 153, 105, 0.3)'
      }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem',
            textDecoration: 'none', fontWeight: '500'
          }}>
            <ChevronLeft size={16} /> Retour au site
          </Link>
          <a href={CALENDLY} target="_blank" rel="noopener noreferrer" style={{
            padding: '0.45rem 1rem', background: 'var(--primary-gold)',
            color: 'white', borderRadius: '2rem', fontSize: '0.8rem',
            fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase',
            textDecoration: 'none'
          }}>
            Rendez-vous gratuit
          </a>
        </div>
      </header>

      {/* Hero block */}
      <section style={{
        background: 'linear-gradient(135deg, var(--primary-brown) 0%, #2a2008 100%)',
        padding: '4rem 0',
        color: 'white'
      }}>
        <div className="container" style={{ maxWidth: '880px' }}>
          <span style={{
            display: 'inline-block',
            fontSize: '0.72rem',
            color: 'var(--primary-gold)',
            fontWeight: '700',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            marginBottom: '1rem'
          }}>
            ActiV Patrimoine · Annecy
          </span>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.85rem, 4.4vw, 2.85rem)',
            fontWeight: '600',
            lineHeight: 1.15,
            marginBottom: '1.25rem',
            color: 'white'
          }}>
            {h1}
          </h1>
          <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.85)', maxWidth: '720px' }}>
            {subtitle}
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <a href={CALENDLY} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.9rem 1.85rem',
              background: 'linear-gradient(135deg, var(--primary-gold) 0%, #c9b576 100%)',
              color: 'white', fontWeight: '700', fontSize: '0.95rem',
              borderRadius: '2.5rem', textDecoration: 'none',
              boxShadow: '0 10px 28px rgba(172,153,105,0.4)'
            }}>
              Bilan patrimonial offert <ArrowRight size={16} />
            </a>
            <a href="tel:+33689300292" style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.9rem 1.5rem',
              border: '1px solid rgba(255,255,255,0.25)',
              color: 'white', fontWeight: '600', fontSize: '0.9rem',
              borderRadius: '2.5rem', textDecoration: 'none'
            }}>
              <Phone size={15} /> 06 89 30 02 92
            </a>
          </div>
        </div>
      </section>

      {/* Content sections */}
      <section style={{ padding: '4rem 0', background: 'var(--bg-page)' }}>
        <div className="container" style={{ maxWidth: '820px' }}>
          {sections.map((sec, idx) => (
            <div key={idx} style={{ marginBottom: '3rem' }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.4rem, 3vw, 1.85rem)',
                fontWeight: '600',
                color: 'var(--primary-brown)',
                marginBottom: '1rem',
                lineHeight: 1.25
              }}>
                {sec.title}
              </h2>
              <p style={{
                fontSize: '1rem',
                lineHeight: 1.85,
                color: 'var(--text-body)',
                marginBottom: sec.bullets ? '1rem' : 0
              }}>
                {sec.body}
              </p>
              {sec.bullets && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {sec.bullets.map((b, i) => (
                    <li key={i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
                      marginBottom: '0.6rem',
                      fontSize: '0.98rem', lineHeight: 1.7,
                      color: 'var(--text-body)'
                    }}>
                      <CheckCircle size={18} style={{ color: 'var(--primary-gold)', marginTop: '3px', flexShrink: 0 }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* FAQ */}
          {faq.length > 0 && (
            <div style={{ marginTop: '4rem', paddingTop: '3rem', borderTop: '1px solid var(--border-light)' }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.4rem, 3vw, 1.85rem)',
                fontWeight: '600',
                color: 'var(--primary-brown)',
                marginBottom: '2rem'
              }}>Questions fréquentes</h2>
              {faq.map(({ q, a }, idx) => (
                <details key={idx} style={{
                  marginBottom: '1rem',
                  padding: '1.25rem 1.5rem',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0.85rem',
                  cursor: 'pointer'
                }}>
                  <summary style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: '1.05rem',
                    fontWeight: '600',
                    color: 'var(--primary-brown)',
                    listStyle: 'none'
                  }}>{q}</summary>
                  <p style={{
                    marginTop: '0.75rem',
                    fontSize: '0.95rem',
                    lineHeight: 1.75,
                    color: 'var(--text-body)'
                  }}>{a}</p>
                </details>
              ))}
            </div>
          )}

          {/* Final CTA */}
          <div style={{
            marginTop: '4rem',
            padding: '2.5rem 2rem',
            background: 'linear-gradient(135deg, var(--primary-brown) 0%, #2a2008 100%)',
            borderRadius: '1rem',
            textAlign: 'center',
            color: 'white'
          }}>
            <h3 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(1.3rem, 3vw, 1.75rem)',
              fontWeight: '600',
              marginBottom: '0.75rem'
            }}>Profitez d'un bilan patrimonial offert</h3>
            <p style={{
              fontSize: '0.95rem',
              color: 'rgba(255,255,255,0.8)',
              marginBottom: '1.75rem',
              maxWidth: '560px',
              margin: '0 auto 1.75rem'
            }}>
              Échange offert, sans engagement, à Annecy ou en visio partout en France.
            </p>
            <a href={CALENDLY} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
              padding: '1rem 2.25rem',
              background: 'linear-gradient(135deg, var(--primary-gold) 0%, #c9b576 100%)',
              color: 'white', fontWeight: '700', fontSize: '0.95rem',
              letterSpacing: '0.04em', textTransform: 'uppercase',
              borderRadius: '2.5rem', textDecoration: 'none',
              boxShadow: '0 12px 32px rgba(172,153,105,0.45)'
            }}>
              Réserver mon échange offert <ArrowRight size={18} />
            </a>
            <p style={{ marginTop: '1.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
              <Mail size={12} style={{ display: 'inline', marginRight: '0.3rem', verticalAlign: 'middle' }} />
              <a href="mailto:arthur.vacogne@gmail.com" style={{ color: 'rgba(255,255,255,0.8)' }}>arthur.vacogne@gmail.com</a>
              {" · "}
              <Phone size={12} style={{ display: 'inline', marginRight: '0.3rem', verticalAlign: 'middle' }} />
              06 89 30 02 92
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeoLandingPage;
